import { useMemo } from 'react';
import { Link } from 'react-router';
import { useAppointmentProposalOptionModifiers } from '../../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { SectionWrapper } from '../../../patterns';
import SyncStatusSection from './sections/SyncStatusSection';

const SyncStatusPage: React.FC = () => {
  const { options, isLoading, updateAppointmentProposalOption } =
    useAppointmentProposalOptions();
  const {
    proposalProducts,
    isLoading: isLoadingProducts,
    updateAppointmentProposalOptionProduct,
  } = useAppointmentProposalOptionProducts();
  const {
    proposalModifiers,
    isLoading: isLoadingModifiers,
    updateAppointmentProposalOptionModifier,
  } = useAppointmentProposalOptionModifiers();
  const { currentProposal } = useAppointmentProposals();

  const optionsWithErrors = useMemo(
    () =>
      [proposalModifiers, proposalProducts].flat().reduce(
        (acc, curr) => {
          const errorMessaege =
            curr.errorDetail?.message || curr.errorDetail?.type;

          return errorMessaege
            ? acc.map((item) =>
                item.optionId === curr.appointmentProposalOptionId
                  ? {
                      ...item,
                      errors: [...new Set([errorMessaege, ...item.errors])],
                      isSelected: true,
                    }
                  : item
              )
            : acc;
        },
        options.map((option) => ({
          optionId: option.appointmentProposalOptionId,
          errors: [option.errorDetail?.message].flatMap((m) => m || []),
          isSelected: !!option.errorDetail?.message,
          name: option.name,
          estimateUrls: option.externalIds.flatMap((id) =>
            id.name === 'estimateUrl' && typeof id.id === 'string'
              ? [id.id]
              : []
          ),
        }))
      ),
    [options, proposalModifiers, proposalProducts]
  );

  if (!currentProposal) {
    return null;
  }

  const idsWithErrors = new Set(
    optionsWithErrors.filter((o) => o.errors.length).map((o) => o.optionId)
  );

  const handleProposalSync = async (optionIds: string[]) =>
    Promise.all(
      optionIds.map((optionId) =>
        Promise.all(
          [
            options
              .filter(
                (o) =>
                  o.appointmentProposalOptionId === optionId && !!o.errorDetail
              )
              .map((option) =>
                updateAppointmentProposalOption(
                  currentProposal.appointmentId,
                  currentProposal.appointmentProposalId,
                  option.appointmentProposalOptionId,
                  { errorDetail: null }
                )
              ),
            proposalProducts
              .filter(
                (p) =>
                  p.appointmentProposalOptionId === optionId && !!p.errorDetail
              )
              .map((product) =>
                updateAppointmentProposalOptionProduct(
                  currentProposal.appointmentId,
                  currentProposal.appointmentProposalId,
                  optionId,
                  product.appointmentProposalOptionProductId,
                  { errorDetail: null }
                )
              ),
            proposalModifiers
              .filter(
                (p) =>
                  p.appointmentProposalOptionId === optionId && !!p.errorDetail
              )
              .map((modifier) =>
                updateAppointmentProposalOptionModifier(
                  currentProposal.appointmentId,
                  currentProposal.appointmentProposalId,
                  optionId,
                  modifier.appointmentProposalOptionModifierId,
                  { errorDetail: null }
                )
              ),
          ].flat()
        )
      )
    );

  return (
    <SectionWrapper
      maxWidth={'sm'}
      width={'100%'}
      header="Service Titan Estimate Sync"
      subheader="Open and view proposal options in Service Titan."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../',
          label: 'Cancel',
        },
        ...(!!idsWithErrors.size && {
          right: {
            size: 'large',
            type: 'button',
            label: 'Sync failed, tap to retry',
            disabled: isLoading || isLoadingProducts || isLoadingModifiers,
            onClick: () => handleProposalSync(Array.from(idsWithErrors)),
          },
        }),
      }}
    >
      <SyncStatusSection options={optionsWithErrors} />
    </SectionWrapper>
  );
};

export default SyncStatusPage;
